import { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { 
  ArrowLeft,
  Activity, 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  User,
  Monitor,
  Globe,
  Clock,
  ShieldCheck,
  MapPin,
  Smartphone,
  Laptop,
  Tablet,
  FileText,
  Hash,
  Calendar,
  Info
} from "lucide-react"
import Loader from "@/components/loader/loader"
import { toast } from "sonner"
import { useAdminAuth } from "@/context/Admin"

export default function ActivityLogDetails() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { admin, getAuditLogById } = useAdminAuth()
  const isSuperAdmin = admin?.role === "super_admin"
  
  const [log, setLog] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchLogDetails = async () => {
      if (!isSuperAdmin || !id) return
      
      try {
        setLoading(true)
        setError(null)
        const response = await getAuditLogById(id)
        if (response?.data) {
          setLog(response.data)
        }
      } catch (err) {
        const message = err?.message || "Failed to fetch activity log details"
        setError(message)
        toast.error(message)
      } finally {
        setLoading(false)
      }
    }

    fetchLogDetails()
  }, [id, isSuperAdmin, getAuditLogById])

  const getStatusBadge = (status) => {
    if (status === "success") {
      return (
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-sm px-3 py-1">
          <CheckCircle className="w-4 h-4 mr-1.5" />
          Success
        </Badge>
      )
    }
    return (
      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-sm px-3 py-1">
        <XCircle className="w-4 h-4 mr-1.5" />
        Failed
      </Badge>
    )
  }

  const getActionBadge = (action) => {
    const actionColors = {
      login: "bg-blue-50 text-blue-700 border-blue-200",
      logout: "bg-gray-50 text-gray-700 border-gray-200",
      create_admin: "bg-green-50 text-green-700 border-green-200",
      delete_admin: "bg-red-50 text-red-700 border-red-200",
      update_admin: "bg-yellow-50 text-yellow-700 border-yellow-200",
      password_change: "bg-purple-50 text-purple-700 border-purple-200",
      password_reset: "bg-orange-50 text-orange-700 border-orange-200",
      revoke_session: "bg-pink-50 text-pink-700 border-pink-200",
      block_ip: "bg-red-50 text-red-700 border-red-200",
      unblock_ip: "bg-green-50 text-green-700 border-green-200",
    }

    const colorClass = actionColors[action] || "bg-gray-50 text-gray-700 border-gray-200"
    const displayAction = action?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()) || "Unknown"

    return (
      <Badge variant="outline" className={`${colorClass} text-sm px-3 py-1`}>
        {displayAction}
      </Badge>
    )
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
  }

  const getDeviceIcon = (deviceType) => {
    const type = deviceType?.toLowerCase() || ""
    if (type.includes("mobile") || type.includes("phone")) {
      return <Smartphone className="w-5 h-5" />
    }
    if (type.includes("tablet")) {
      return <Tablet className="w-5 h-5" />
    }
    return <Laptop className="w-5 h-5" />
  }

  // Restrict access to super_admin only
  if (!isSuperAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 px-2 sm:px-4 lg:px-8">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-600" />
          <h1 className="text-2xl sm:text-3xl font-bold text-red-600 mb-2">
            Access Denied
          </h1>
          <p className="text-sm sm:text-base text-gray-600">
            You do not have permission to access this page. Only super administrators can view activity log details.
          </p>
        </div>
      </div>
    )
  }

  if (loading) {
    return <Loader />
  }

  if (error || !log) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <AlertCircle className="w-16 h-16 text-red-500" />
        <h2 className="text-xl font-semibold text-gray-700">{error || "Activity log not found"}</h2>
        <Button onClick={() => navigate("/admin/activity-log")} variant="outline">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Activity Log
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate("/admin/activity-log")}
            className="mr-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-primary">Activity Log Details</h1>
              <p className="text-gray-600 mt-1">Log ID: #{log.id}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {getStatusBadge(log.status)}
          {getActionBadge(log.action)}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Action Overview */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-primary" />
                Action Overview
              </CardTitle>
              <CardDescription>Details about the action that was performed</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Activity className="w-4 h-4" />
                    Action Type
                  </label>
                  <p className="text-lg">{getActionBadge(log.action)}</p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Status
                  </label>
                  <p className="text-lg">{getStatusBadge(log.status)}</p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Resource Type
                  </label>
                  <p className="text-lg font-medium">{log.resource_type || "N/A"}</p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Hash className="w-4 h-4" />
                    Resource ID
                  </label>
                  <p className="text-lg font-mono">{log.resource_id || "N/A"}</p>
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  Description
                </label>
                <p className="text-base bg-gray-50 p-4 rounded-lg">
                  {log.description || "No description available for this action."}
                </p>
              </div>

              {log.error_message && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <label className="text-sm font-medium text-red-700 flex items-center gap-2 mb-2">
                    <AlertCircle className="w-4 h-4" />
                    Error Message
                  </label>
                  <p className="text-sm text-red-600">{log.error_message}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Device Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Monitor className="w-5 h-5 text-primary" />
                Device Information
              </CardTitle>
              <CardDescription>Details about the device and browser used</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">Device Type</label>
                  <div className="flex items-center gap-2">
                    {getDeviceIcon(log.device_type)}
                    <span className="text-base font-medium capitalize">{log.device_type || "Unknown"}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">Browser</label>
                  <p className="text-base font-medium">{log.browser || "Unknown"}</p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">Operating System</label>
                  <p className="text-base font-medium">{log.os || "Unknown"}</p>
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">Full User Agent</label>
                <p className="text-sm text-muted-foreground bg-gray-50 p-4 rounded-lg font-mono break-all">
                  {log.user_agent || "N/A"}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Metadata */}
          {log.metadata && Object.keys(log.metadata).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  Additional Metadata
                </CardTitle>
                <CardDescription>Extra information recorded with this action</CardDescription>
              </CardHeader>
              <CardContent>
                <pre className="p-4 bg-gray-50 rounded-lg overflow-x-auto text-sm font-mono">
                  {JSON.stringify(log.metadata, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Admin Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Admin Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-lg">{log.admin?.name || "Unknown"}</p>
                  <p className="text-sm text-muted-foreground">{log.admin?.email || "N/A"}</p>
                </div>
              </div>
              {log.admin?.role && (
                <div className="pt-2">
                  <Badge variant="secondary" className="capitalize">
                    {log.admin.role.replace(/_/g, " ")}
                  </Badge>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Location Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-primary" />
                Location & Network
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  IP Address
                </label>
                <p className="text-base font-mono bg-gray-50 px-3 py-2 rounded-md">{log.ip_address || "N/A"}</p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Location
                </label>
                <p className="text-base">{log.location || "Unknown"}</p>
              </div>
            </CardContent>
          </Card>

          {/* Timestamp */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                Timestamp
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Date & Time
                </label>
                <p className="text-base">{formatDate(log.created_at)}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
